import React from "react";
import { Icon } from "rhombus-react";

import useQueryParams from "hooks/useQueryParams";
import { SUPPORTED_AD_TYPES } from "widgets/actions/PredictiveScoringAction";

// The two right-side preview panels are toggled via URL query params. Both the creative and version
// preview toolbars drive the same two panels with the same "one panel at a time" rule, so the param
// keys and the toggle logic live here once — a change to either only has to happen in this file.
export const SHOW_SCORE_PARAM = "showScore";
export const SHOW_LABELS_PARAM = "showLabels";

// A panel is shown only when its param is explicitly "true" (hidden by default). Opening one panel
// closes the other so they can never render side by side. Returns the current visibility plus a
// toggle that enforces the mutual exclusion.
export function useExclusivePanelToggle(param, otherParam) {
	const { getParam, setParam } = useQueryParams();
	const isVisible = getParam(param) === "true";

	function toggle() {
		// Only clear the other panel when turning THIS one on; leave it alone when turning off.
		if (!isVisible) {
			setParam(otherParam, "false");
		}
		setParam(param, isVisible ? "false" : "true", true);
	}

	return { isVisible, toggle };
}

// Shared verbatim by both preview toolbars: the gauge icon that toggles the Predictive Score panel.
// Gated on the scoring feature flag AND ad-type eligibility (the same SUPPORTED_AD_TYPES gate the
// bulk-scoring action uses) so it never appears as a dead control on creatives that can't be scored
// — e.g. social previews, whose PreviewBlade never renders the panel. Approval status is not checked
// here: this toggles the view of a score, which is independent of whether the creative is approved.
export const PREDICTIVE_SCORE_ACTION = {
	shouldDisplay({ isPredictiveScoringOn, adType }) {
		return isPredictiveScoringOn && SUPPORTED_AD_TYPES.has(adType);
	},
	component: () => {
		const { isVisible: showScore, toggle } = useExclusivePanelToggle(SHOW_SCORE_PARAM, SHOW_LABELS_PARAM);

		return (
			<div onClick={toggle}>
				<Icon
					name={showScore ? "fas fa-gauge-high active" : "far fa-gauge-high"}
					data-tip="Predictive Score"
					data-place="left"
				/>
			</div>
		);
	},
};
